import { FocusEvent, useEffect, useMemo } from "react";
import { FormikErrors, FormikTouched, Formik, FormikProps } from "formik";
import * as Yup from "yup";
import {
    getFormattedAddress,
    getInitialLocation,
    getManualComponents,
    getYupSchemaForGoogleAddressComponents
} from "@utils/address-util";
import { AttributeFormState } from "@idw-types/state/attribute-form-state";
import { AddressComponentInput } from "@containers/inputs/international-address-input/manual-international-address-input/address-component-input/address-component-input";
import { GoogleAddressComponentDisplay } from "@iddataweb/idw-common-ui/types";
import { InternationalAddressCountryTypes } from "@iddataweb/idw-common-ui/enums";

type ManualInternationalAddressInputContainerProps = {
    googleAddressComponents: Array<GoogleAddressComponentDisplay>;
    attribute: AttributeFormState;
    countryCode: InternationalAddressCountryTypes;
    isSubmitting: boolean;
    fieldName: string;
    onBlur: (event: FocusEvent<HTMLInputElement>) => void;
    setAddressObject: ({ address, components }: { address: string; components: any }) => void;
    setFieldValue: (field: string, value: any, shouldValidate?: boolean) => Promise<void> | Promise<FormikErrors<any>>;
    setFieldTouched: (field: string, isTouched?: boolean, shouldValidate?: boolean) => void;
    onSetSubmitValue: (data: any) => void;
};

export const ManualInternationalAddressInputContainer = (props: ManualInternationalAddressInputContainerProps) => {
    const {
        googleAddressComponents,
        attribute,
        countryCode,
        isSubmitting,
        fieldName,
        onBlur,
        setAddressObject,
        setFieldValue,
        setFieldTouched,
        onSetSubmitValue
    } = props;

    const isRequired = useMemo(() => !(attribute.isOptional || attribute.optional), [attribute.isOptional, attribute.optional]);

    const manualAddressSchema = useMemo(() => {
        if (isRequired) {
            return Yup.object().shape(getYupSchemaForGoogleAddressComponents(googleAddressComponents))
        }

        return null;
    }, [isRequired, googleAddressComponents]);

    const initialValues = useMemo(() => getInitialLocation(countryCode).components, [countryCode]);

    const setFormValues = async () => {
        const components = getManualComponents(attribute.inputData);
        const formattedAddress = getFormattedAddress(components, googleAddressComponents);
        setAddressObject({ ...location, address: formattedAddress, components });
        if (formattedAddress) {
            await setFieldValue(fieldName, formattedAddress);
        } else {
            await setFieldValue(fieldName, '');
        }
        setFieldTouched(fieldName, true, true);
    };

    const validateSchema = async () => {
        const isValid = await manualAddressSchema.isValid(attribute.inputData);
        if (isValid) {
            setFormValues();
        } else {
            const components = getManualComponents(attribute.inputData);
            setAddressObject({ ...location, address: "", components });
            await setFieldValue(fieldName, "");
            setFieldTouched(fieldName, true, true);
        }
    }

    useEffect(() => {
        if (manualAddressSchema) {
            validateSchema();
        } else {
            setFormValues();
        }
    }, [
        attribute.inputData.country,
        attribute.inputData.locality,
        attribute.inputData.route,
        attribute.inputData.street_number,
        attribute.inputData.postal_code,
        attribute.inputData.subpremise,
        attribute.inputData.premise,
        attribute.inputData.administrative_area_level_1,
        attribute.inputData.administrative_area_level_2,
        attribute.inputData.administrative_area_level_3
    ]);

    const renderManualInternationalAddressInputContainer = (
        errors: FormikErrors<any>,
        touched: FormikTouched<any>,
        setFieldTouched: (field: string, isTouched?: boolean, shouldValidate?: boolean) => void,
        setFieldValue: (field: string, value: any, shouldValidate?: boolean) => Promise<void> | Promise<FormikErrors<any>>
    ) => {
        return (
            <div>
                {googleAddressComponents.map(addressComponent => (
                    <AddressComponentInput
                        key={`manual-int-address-input-${addressComponent.name}`}
                        countryCode={countryCode}
                        addressComponent={addressComponent}
                        attribute={attribute}
                        errors={errors}
                        onBlur={onBlur}
                        setFieldTouched={setFieldTouched}
                        setFieldValue={setFieldValue}
                        touched={touched}
                        isSubmitting={isSubmitting}
                        onSetSubmitValue={onSetSubmitValue}
                    />
                ))}
            </div>
        );
    };

    return (
        <Formik
            initialValues={initialValues}
            validateOnMount={true}
            validationSchema={manualAddressSchema}
            onSubmit={undefined}
        >
            {({ errors, touched, setFieldTouched, setFieldValue }: FormikProps<any>) => (
                renderManualInternationalAddressInputContainer(
                    errors,
                    touched,
                    setFieldTouched,
                    setFieldValue
                )
            )}
        </Formik>
    );
};